// src/pages/public/FaqPage.jsx
import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { faqApi } from '@/api/services'
import { ChevronDown, ThumbsUp, Search, HelpCircle } from 'lucide-react'

export default function FaqPage() {
  const [search, setSearch] = useState('')
  const [openId, setOpenId] = useState(null)
  const [activeCategory, setActiveCategory] = useState(null)

  const { data, isLoading } = useQuery({
    queryKey: ['faqs'],
    queryFn: () => faqApi.list().then(r => r.data),
  })

  const helpfulMut = useMutation({
    mutationFn: (id) => faqApi.helpful(id),
  })

  const allFaqs = data?.data?.faqs ?? []
  const categories = data?.data?.categories ?? []

  const filtered = allFaqs.filter(faq => {
    const matchSearch = !search || faq.question.toLowerCase().includes(search.toLowerCase()) || faq.answer.toLowerCase().includes(search.toLowerCase())
    const matchCat = !activeCategory || faq.category === activeCategory
    return matchSearch && matchCat
  })

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <div className="text-center mb-8">
        <HelpCircle className="w-12 h-12 mx-auto mb-3 text-pink-500" />
        <h1 className="text-3xl font-bold text-gray-900">Frequently Asked Questions</h1>
        <p className="text-gray-500 mt-2">Find answers to common questions about Fabrinza</p>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search FAQs..."
          className="w-full pl-10 pr-4 py-3 border rounded-xl text-sm focus:ring-2 focus:ring-pink-200 focus:border-pink-400 outline-none"
        />
      </div>

      {/* Category tabs */}
      {categories.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setActiveCategory(null)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${!activeCategory ? 'bg-pink-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            All
          </button>
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition ${activeCategory === cat ? 'bg-pink-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {/* FAQ accordion */}
      {isLoading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <p>No FAQs found</p>
          {search && <p className="text-xs mt-1">Try a different search term</p>}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(faq => (
            <div key={faq.id} className="bg-white rounded-lg border overflow-hidden">
              <button
                onClick={() => setOpenId(openId === faq.id ? null : faq.id)}
                className="w-full px-5 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition"
              >
                <span className="font-medium text-gray-900 text-sm pr-4">{faq.question}</span>
                <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${openId === faq.id ? 'rotate-180' : ''}`} />
              </button>
              {openId === faq.id && (
                <div className="px-5 pb-4">
                  <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">{faq.answer}</p>
                  <button
                    onClick={() => helpfulMut.mutate(faq.id)}
                    disabled={helpfulMut.isPending}
                    className="mt-3 flex items-center gap-1 text-xs text-gray-400 hover:text-pink-500 transition"
                  >
                    <ThumbsUp className="w-3.5 h-3.5" />
                    Helpful ({faq.helpful_count})
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
